import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CalendarIcon, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useClinicAuth } from "../context/ClinicAuthContext";
import { fetchProducts, clinicCreateOrder, formatUsd } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";

export default function ClinicNewOrderPage() {
  const { clinic, loading } = useClinicAuth();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [variants, setVariants] = useState({});
  const [patient, setPatient] = useState({ name: "", email: "", notes: "", address: "" });
  const [date, setDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => { fetchProducts().then(setProducts); }, []);

  const product = useMemo(() => products.find((p) => p.id === selected), [products, selected]);
  const { total, swaps } = useMemo(() => {
    if (!product) return { total: 0, swaps: [] };
    let delta = 0; const sw = [];
    product.included.forEach((item, i) => {
      const optIdx = variants[i] ?? 0;
      if (optIdx > 0) {
        const a = (item.alternatives || [])[optIdx - 1];
        if (a) { delta += a.msrp - item.msrp; sw.push({ brand: a.brand, name: a.name }); }
      }
    });
    return { total: product.price + delta, swaps: sw };
  }, [product, variants]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;
  if (!clinic) return <Navigate to="/clinics/login" replace />;

  const submit = async () => {
    if (!selected || !date || !patient.name) {
      toast.error("Add a patient name, procedure and surgery date.");
      return;
    }
    setBusy(true);
    try {
      const order = await clinicCreateOrder({
        patient_name: patient.name,
        patient_email: patient.email || undefined,
        surgery_date: format(date, "yyyy-MM-dd"),
        product_id: selected,
        variants,
        surgeon_notes: patient.notes,
        delivery_address: patient.address,
      });
      toast.success(`Order ${order.order_ref} placed`);
      nav("/clinics/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
      <Link to="/clinics/dashboard" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#6B5757] hover:text-[#2A1F1F]">
        <ArrowLeft className="w-3 h-3" /> Dashboard
      </Link>
      <div className="mt-6">
        <div className="eyebrow">New Patient Order</div>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
          Configure a system for a specific patient.
        </h1>
      </div>

      {/* Patient */}
      <section className="mt-12 soft-card p-8">
        <div className="eyebrow">Step 01 · Patient</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Patient Name</span>
            <input data-testid="new-order-patient-name" value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })}
              className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]" required />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Patient Email (optional)</span>
            <input data-testid="new-order-patient-email" type="email" value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })}
              className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]" />
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="new-order-date-trigger" className="text-left px-4 py-3 mt-4 md:mt-4 text-sm bg-white border border-[#E5CDBB] rounded-md flex items-center justify-between">
                <span className={date ? "" : "text-[#A08D8D]"}>{date ? format(date, "PPP") : "Select surgery date"}</span>
                <CalendarIcon className="w-4 h-4 text-[#6B5757]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-[#E5CDBB]" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
            </PopoverContent>
          </Popover>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Delivery Address (optional)</span>
            <input data-testid="new-order-address" value={patient.address} onChange={(e) => setPatient({ ...patient, address: e.target.value })}
              className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
              placeholder="Ship-to address for pre-op delivery" />
          </label>
        </div>
        <label className="block mt-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Surgeon Notes (shown to patient on tracking page)</span>
          <textarea data-testid="new-order-notes" value={patient.notes} onChange={(e) => setPatient({ ...patient, notes: e.target.value })}
            rows={3} className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
            placeholder="e.g. Begin scar sheets on Day 14 not Day 10." />
        </label>
      </section>

      {/* Procedure */}
      <section className="mt-8 soft-card p-8">
        <div className="eyebrow">Step 02 · Procedure</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          {products.map((p) => (
            <button key={p.id}
              data-testid={`new-order-procedure-${p.id}`}
              onClick={() => { setSelected(p.id); setVariants({}); }}
              className={`text-left px-5 py-4 border rounded-md transition-colors ${
                selected === p.id ? "border-[#2A1F1F] bg-[#2A1F1F] text-[#FBF6F1]" : "border-[#E5CDBB] bg-white hover:border-[#2A1F1F]"
              }`}
            >
              <div className={`font-serif text-lg ${selected === p.id ? "text-[#FBF6F1]" : "text-[#2A1F1F]"}`}>{p.procedure}</div>
              <div className={`text-xs mt-1 ${selected === p.id ? "text-[#E5C1AF]" : "text-[#6B5757]"}`}>
                {formatUsd(p.price)} · {p.duration_weeks}-week protocol
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Configure */}
      {product && (
        <section className="mt-8 soft-card p-8">
          <div className="flex flex-col sm:flex-row justify-between gap-4 items-start sm:items-end">
            <div>
              <div className="eyebrow">Step 03 · Configure the Bundle</div>
              <p className="text-sm text-[#6B5757] mt-2 max-w-md">Swap in the brands you prescribe most.</p>
            </div>
            <div className="text-right">
              <div className="eyebrow">Total</div>
              <div data-testid="new-order-total" className="font-serif text-3xl mt-1">{formatUsd(total)}</div>
              {swaps.length > 0 && (
                <div className="text-xs text-[#B85C56] mt-1">{swaps.length} swap{swaps.length > 1 ? "s" : ""}</div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {product.included.map((item, slotIdx) => {
              const options = [item, ...(item.alternatives || [])];
              const current = variants[slotIdx] ?? 0;
              const chosen = options[current];
              return (
                <div key={slotIdx} className="bg-white border border-[#F0DFD0] p-4 rounded-md">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#B85C56]">Slot 0{slotIdx + 1} · {item.category}</div>
                  <div className="font-serif text-base mt-1 text-[#2A1F1F]">{chosen.name}</div>
                  <div className="text-xs text-[#6B5757]">{chosen.brand} · {formatUsd(chosen.msrp)}</div>
                  {options.length > 1 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {options.map((o, oi) => {
                        const active = oi === current;
                        return (
                          <button key={oi}
                            data-testid={`new-order-slot-${slotIdx}-opt-${oi}`}
                            onClick={() => setVariants({ ...variants, [slotIdx]: oi })}
                            className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                              active ? "bg-[#2A1F1F] text-[#FBF6F1] border-[#2A1F1F]" : "bg-white border-[#E5CDBB] hover:border-[#2A1F1F]"
                            }`}
                          >
                            {active && <Check className="w-3 h-3 inline mr-1" />}{o.brand}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-10 flex justify-end">
        <button data-testid="new-order-submit" onClick={submit} disabled={busy || !selected || !date || !patient.name}
          className="btn-primary inline-flex items-center gap-3 disabled:opacity-60">
          {busy ? "Placing…" : "Place Order"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
